import { Square } from 'lucide-react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { computeQueryExecutionEnergyState } from '../utils/queryExecutionEnergy';

export type GridStatusPhase =
  | { kind: 'awaiting' }
  | { kind: 'populating'; loadedRowCount: number; finalRowCount?: number }
  | { kind: 'idle' };

export interface GridStatusInlineProps {
  phase: GridStatusPhase;
  onCancel?: () => void;
  /**
   * True once a cancel is outstanding. Relabels Stop to "Stopping…" and offers
   * the force-stop in its title — the button stays clickable, and that second
   * click is what the host turns into a forced cancel.
   */
  isCancelling?: boolean;
  /**
   * When true (set after the long-running threshold is crossed), the awaiting
   * label changes from "Executing query…" to "Executing query… taking longer"
   * and the row shifts to amber to draw the user's eye to the Stop affordance.
   */
  isLongRunning?: boolean;
  testIdAwaiting?: string;
  testIdPopulating?: string;
  /**
   * Compact rendering — drops the "Loading rows… " / "Loading… " prefix from the
   * populating phase, leaving just `<spinner> X received` or `<spinner> X / Y`.
   * Useful when the host has limited horizontal space (e.g. the Table Explorer
   * pagination strip, where the row-count slot sits next to the page-size selector).
   */
  compact?: boolean;
}

export function GridStatusInline({
  phase,
  onCancel,
  isCancelling = false,
  isLongRunning = false,
  testIdAwaiting = 'grid-status-awaiting',
  testIdPopulating = 'grid-status-populating',
  compact = false,
}: GridStatusInlineProps) {
  if (phase.kind === 'idle') return null;

  const stopButton = onCancel ? (
    <>
      <span className="mx-1 text-muted-foreground/60">|</span>
      <button
        type="button"
        onClick={onCancel}
        // Deliberately NOT disabled while cancelling: a polite cancel can sit
        // unhonoured for a long time (an Oracle session ignoring OCIBreak, for
        // one), and the second press is the user's escalation to a force-stop
        // that drops the CONNECTION. Disabling it left no way out but waiting.
        className="inline-flex items-center gap-1 hover:underline"
        title={isCancelling ? 'Stopping… click again to force the stop (Oracle: drops the connection so the query returns at once — the server may still finish what it already started; other engines keep their normal cancel)' : 'Stop'}
        data-testid="grid-status-stop"
      >
        <Square className="h-3 w-3 fill-current" />
        {isCancelling ? 'Stopping…' : 'Stop'}
      </button>
    </>
  ) : null;

  if (phase.kind === 'awaiting') {
    // CH-28 — energized only once the long-running threshold is crossed
    // (computeQueryExecutionEnergyState); neutral (blue, "in progress")
    // otherwise. The spinner is a glyph (--energy); the label is small text,
    // so it takes the AA-rated --energy-text variant.
    const energized = computeQueryExecutionEnergyState(isLongRunning) === 'energy';
    const colorClass = energized
      ? 'text-energy-text'
      : 'text-blue-600 dark:text-blue-400';
    const spinnerClass = energized
      ? 'text-energy flex-shrink-0'
      : 'text-blue-500 dark:text-blue-400 flex-shrink-0';
    return (
      <span
        className={`flex items-center gap-1.5 ${colorClass}`}
        data-testid={testIdAwaiting}
        data-long-running={isLongRunning || undefined}
      >
        <RandomDotmSpinner size={12} className={spinnerClass} />
        {isLongRunning ? 'Executing query… taking longer' : 'Executing query…'}
        {stopButton}
      </span>
    );
  }

  // populating
  const { loadedRowCount, finalRowCount } = phase;
  const text = compact
    ? finalRowCount != null
      ? `${loadedRowCount.toLocaleString()} / ${finalRowCount.toLocaleString()}`
      : `${loadedRowCount.toLocaleString()} received`
    : finalRowCount != null
      ? `Loading… ${loadedRowCount.toLocaleString()} / ${finalRowCount.toLocaleString()} rows`
      : `Loading rows… ${loadedRowCount.toLocaleString()} received`;

  return (
    <span
      className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 tabular-nums"
      data-testid={testIdPopulating}
    >
      <RandomDotmSpinner size={12} className="text-blue-500 dark:text-blue-400 flex-shrink-0" />
      {text}
      {stopButton}
    </span>
  );
}
